import React, { useEffect, useState } from 'react';
import { Send, Upload, X, CheckCircle2, XCircle, Share2, Clock, CalendarClock } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import RecurrencePicker from '../../components/social/RecurrencePicker';

const PLATFORM_LABELS = { instagram: 'Instagram', linkedin: 'LinkedIn', facebook: 'Facebook', x: 'X', threads: 'Threads' };

export default function CrossPostPage() {
  const [targets, setTargets] = useState([]);
  const [selected, setSelected] = useState({});
  const [caption, setCaption] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]); // [{ file, preview }]
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledConfirmation, setScheduledConfirmation] = useState(null);
  const [recurrence, setRecurrence] = useState({ frequency: 'none' });

  useEffect(() => {
    loadTargets();
  }, []);

  const loadTargets = async () => {
    setLoadingTargets(true);
    setError('');
    try {
      const res = await api.get('/social/targets');
      const list = res.data.data || [];
      setTargets(list);
      const initial = {};
      list.forEach((t) => {
        initial[targetKey(t)] = true;
      });
      setSelected(initial);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load connected accounts');
    } finally {
      setLoadingTargets(false);
    }
  };

  const targetKey = (t) => `${t.platform}:${t.id}`;

  const toggleTarget = (t) => {
    setSelected((prev) => ({ ...prev, [targetKey(t)]: !prev[targetKey(t)] }));
  };

  const MAX_MEDIA = 10;

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setMediaFiles((prev) =>
      [...prev, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))].slice(0, MAX_MEDIA)
    );
    e.target.value = '';
  };

  const removeMedia = (index) => {
    setMediaFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const submit = async () => {
    const chosen = targets.filter((t) => selected[targetKey(t)]);
    if (!chosen.length) return;
    if (!mediaFiles.length && !caption.trim()) {
      setError('Add a media file and/or a caption before publishing');
      return;
    }
    if (scheduleEnabled && !scheduledAt) {
      setError('Pick a date and time to schedule for');
      return;
    }
    if (scheduleEnabled && new Date(scheduledAt).getTime() <= Date.now()) {
      setError('Scheduled time must be in the future');
      return;
    }
    if (scheduleEnabled && recurrence.frequency === 'weekly' && !recurrence.daysOfWeek?.length) {
      setError('Pick at least one day of the week to repeat on');
      return;
    }
    if (scheduleEnabled && recurrence.frequency !== 'none' && recurrence.endType === 'on' && !recurrence.endDate) {
      setError('Pick an end date for the recurrence');
      return;
    }
    setSubmitting(true);
    setError('');
    setResults(null);
    setScheduledConfirmation(null);
    try {
      const form = new FormData();
      form.append('caption', caption);
      form.append('targets', JSON.stringify(chosen.map((t) => ({ platform: t.platform, id: t.id, accountId: t.accountId }))));
      mediaFiles.forEach(({ file }) => form.append('media', file));
      if (scheduleEnabled) {
        form.append('scheduledAt', new Date(scheduledAt).toISOString());
        if (recurrence.frequency !== 'none') {
          form.append('recurrence', JSON.stringify({
            ...recurrence,
            endDate: recurrence.endType === 'on' ? new Date(recurrence.endDate).toISOString() : undefined,
          }));
        }
      }

      const res = await api.post('/social/publish', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.data?.scheduled) {
        setScheduledConfirmation(res.data.data.crossPost);
      } else {
        setResults(res.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to publish');
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = targets.reduce((acc, t) => {
    acc[t.platform] = acc[t.platform] || [];
    acc[t.platform].push(t);
    return acc;
  }, {});

  const selectedCount = targets.filter((t) => selected[targetKey(t)]).length;

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Share2 size={18} className="text-[#0176D3]" />
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">Cross-Post</h2>
        </div>

        {error && <div className="text-[13px] text-[#BA0517]">{error}</div>}

        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Write a caption…"
          rows={4}
          className="w-full border border-[#E5E5E5] rounded-[4px] px-3 py-2 text-[13px] resize-none focus:outline-none focus:border-[#0176D3]"
        />

        <div className="flex flex-wrap gap-2">
          {mediaFiles.map(({ file, preview }, i) => (
            <div key={i} className="relative w-24 h-24 rounded-[4px] overflow-hidden border border-[#E5E5E5]">
              {file.type?.startsWith('video/') ? (
                <video src={preview} className="w-full h-full object-cover" />
              ) : (
                <img src={preview} alt="" className="w-full h-full object-cover" />
              )}
              {i === 0 && mediaFiles.length > 1 && (
                <span className="absolute bottom-0.5 left-0.5 bg-black/60 text-white text-[9px] font-bold px-1 rounded">1st</span>
              )}
              <button
                onClick={() => removeMedia(i)}
                className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {mediaFiles.length < MAX_MEDIA && (
            <label className="w-24 h-24 flex items-center justify-center border border-dashed border-[#E5E5E5] rounded-[4px] cursor-pointer hover:border-[#0176D3] text-[#5C5C5C]">
              <input type="file" accept="image/*,video/*" multiple onChange={onFileChange} className="hidden" />
              <Upload size={18} />
            </label>
          )}
        </div>
        {mediaFiles.length > 1 && (
          <div className="text-[11px] text-[#5C5C5C]">
            {mediaFiles.length} media items selected — posted as a carousel/multi-image post on platforms that support it, in the order shown above.
          </div>
        )}
        <div className="text-[11px] text-[#5C5C5C]">
          LinkedIn and Threads carousels support images only; a single video works on Threads; Facebook multi-photo posts are images only too; X is text-only for now (media is skipped).
        </div>

        <div className="pt-2 border-t border-[#E5E5E5] space-y-3">
          <h3 className="text-[13px] font-bold text-[#2E2E2E]">Publish To</h3>
          {loadingTargets && <div className="text-[13px] text-[#5C5C5C]">Loading connected accounts…</div>}
          {!loadingTargets && targets.length === 0 && (
            <div className="text-[13px] text-[#5C5C5C]">
              No connected Instagram or LinkedIn accounts found. Connect an account first.
            </div>
          )}
          {Object.entries(grouped).map(([platform, list]) => (
            <div key={platform} className="space-y-1.5">
              <div className="text-[11px] font-semibold text-[#706E6B]">
                {PLATFORM_LABELS[platform] || platform}
              </div>
              {list.map((t) => (
                <label key={targetKey(t)} className="flex items-center gap-2 text-[13px] text-[#2E2E2E] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!selected[targetKey(t)]}
                    onChange={() => toggleTarget(t)}
                    className="accent-[#0176D3]"
                  />
                  {t.label}
                </label>
              ))}
            </div>
          ))}
        </div>

        <div className="pt-2 border-t border-[#E5E5E5] space-y-3">
          <label className="flex items-center gap-2 text-[13px] text-[#2E2E2E] cursor-pointer">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
              className="accent-[#0176D3]"
            />
            <Clock size={14} className="text-[#5C5C5C]" />
            Schedule for later
          </label>
          {scheduleEnabled && (
            <div className="space-y-3 pl-1">
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                className="border border-[#E5E5E5] rounded-[4px] px-3 py-1.5 text-[13px] focus:outline-none focus:border-[#0176D3]"
              />

              <RecurrencePicker onChange={setRecurrence} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-[#E5E5E5]">
          <Link
            to="/dashboard/social/content-calendar"
            className="flex items-center gap-1 text-[13px] font-semibold text-[#0176D3] hover:underline"
          >
            <CalendarClock size={14} />
            View Content Calendar
          </Link>
          <button
            onClick={submit}
            disabled={submitting || !selectedCount}
            className="flex items-center gap-1 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold px-4 py-2 rounded-[4px] disabled:opacity-50 ml-auto whitespace-nowrap"
          >
            {scheduleEnabled ? <CalendarClock size={14} /> : <Send size={14} />}
            {submitting
              ? (scheduleEnabled ? 'Scheduling…' : 'Publishing…')
              : `${scheduleEnabled ? 'Schedule for' : 'Publish to'} ${selectedCount || ''} target${selectedCount === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      {scheduledConfirmation && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5 flex items-center gap-2 text-[13px]">
          <CalendarClock size={16} className="text-[#0176D3] flex-shrink-0" />
          <span>
            Scheduled for {new Date(scheduledConfirmation.scheduledAt).toLocaleString()} — will publish to{' '}
            {scheduledConfirmation.targets.length} target{scheduledConfirmation.targets.length === 1 ? '' : 's'} automatically.
          </span>
        </div>
      )}

      {results && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5 space-y-2">
          <h3 className="text-[13px] font-bold text-[#2E2E2E]">Results</h3>
          {results.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-[13px]">
              {r.success ? (
                <CheckCircle2 size={16} className="text-[#2E844A] flex-shrink-0" />
              ) : (
                <XCircle size={16} className="text-[#BA0517] flex-shrink-0" />
              )}
              <span className="font-semibold text-[#2E2E2E]">{PLATFORM_LABELS[r.platform] || r.platform}</span>
              <span className="text-[#5C5C5C]">{r.id}</span>
              {!r.success && <span className="text-[#BA0517]">— {r.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
